import React, { useState } from 'react';
import { LockIcon, CheckCircleIcon } from '@phosphor-icons/react';
import { ConfirmModal } from '../common';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import { useNotifications } from '../common';
import { useApiError } from '../../hooks';
import { useEvent } from '../../contexts/EventContext';
import { apiService } from '../../services/api';
import './list-publication-button.css';

interface ListPublicationButtonProps {
  listId: number;
  isLocked: boolean;
  onPublish?: () => void;
  className?: string;
}

const ListPublicationButton: React.FC<ListPublicationButtonProps> = ({
  listId,
  isLocked,
  onPublish,
  className = ''
}) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const { showSuccess } = useNotifications();
  const { handleApiError } = useApiError();
  const { refreshUserListStatus } = useEvent();
  const {
    isOpen,
    config,
    isLoading,
    showConfirm,
    handleConfirm,
    handleCancel,
  } = useConfirmModal();

  const handlePublishClick = async () => {
    const confirmed = await showConfirm({
      title: "Pubblica la tua lista",
      message: "Sei sicuro di voler pubblicare la tua lista? Una volta pubblicata, non potrai più modificare o eliminare i regali già inseriti, ma potrai aggiungerne di nuovi.",
      confirmText: "Pubblica lista",
      confirmVariant: "primary",
    });

    if (confirmed) {
      await handlePublish();
    }
  };

  const handlePublish = async () => {
    try {
      setIsPublishing(true);
      await apiService.lockList(listId);
      
      // Refresh user list status in the event context
      await refreshUserListStatus();
      
      showSuccess(
        "Lista pubblicata!",
        "La tua lista è ora visibile agli altri membri del gruppo."
      );
      
      if (onPublish) {
        onPublish();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsPublishing(false);
    }
  };

  if (isLocked) {
    return (
      <div className={`list-publication-status published ${className}`}>
        <div className="status-icon">
          <CheckCircleIcon size={16} weight="fill" />
        </div>
        <span className="status-text">Lista pubblicata</span>
      </div>
    );
  }

  return (
    <>
      <button
        className={`list-publication-button ${className}`}
        onClick={handlePublishClick}
        disabled={isPublishing}
      >
        <LockIcon size={16} weight="fill" />
        {isPublishing ? 'Pubblicazione...' : 'Rendi pubblica la mia lista'}
      </button>

      <ConfirmModal
        isOpen={isOpen}
        title={config?.title}
        message={config?.message || ""}
        confirmText={config?.confirmText}
        cancelText={config?.cancelText}
        confirmVariant={config?.confirmVariant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    </>
  );
};

export default ListPublicationButton;